import React from 'react';
import { Row } from 'react-table';
import { Loading, Paper } from '@brighterion/br-ui';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import useGetList from 'Hooks/query-hooks/list/useGetList';
import SplitRevisionHistory from 'Components/SplitRevisionHistory';
import SplitViewHeader from 'Components/Table/SplitView/Header';
import SplitView from 'Components/Table/SplitView';
import GlobalRuleDetails from 'Components/GlobalRuleDetails';
import { UseSplitViewForm } from 'Components/Table/hooks/useSplitViewForm';
import SplitRuleStatement from 'Components/SplitRuleStatement';
import TenantGroupOverridePaper from './TenantGroupOverridePaper';
import styles from './SplitViewContent.css';

interface Props extends UseSplitViewForm<GlobalRuleSplitViewFormState> {
    globalRules: GlobalRulesData;
    dataTestId: string;
    rows: Row<Rule>[];
    selectedRow: Row<Rule>;
    readOnly: boolean;
    toggleRowSelected(id: string, toggle: boolean): void;
    tenantGroupId?: number;
    mode?: string;
}

const SplitViewContent = ({
    globalRules,
    dataTestId,
    selectedRow,
    rows,
    readOnly,
    toggleRowSelected,
    splitViewFormState,
    setInitialFormState,
    setIsOpen,
    setRowId,
    tenantGroupId,
    mode
}: Props) => {
    const listAPI = useGetList();
    const { t } = useTranslation();
    const { isOpen } = splitViewFormState;
    const closeSplitView = () => setRowId(null);
    const thisGlobalRule = globalRules.rules.find((grule) => grule.id === (selectedRow.original as Rule).id);
    const { values: selectedRowValues, original } = selectedRow;
    const showTenantGroupPaper = !(readOnly && original.inherit === 'ACQUIRER');
    const ruleIsActive = isOpen || !!(!selectedRowValues.override && thisGlobalRule?.parameters.active);
    const { tenantGroupId: tenantGroupIdString } = useParams<{ tenantGroupId: string }>();
    const currentTenantGroupId = Number(tenantGroupIdString ?? tenantGroupId);

    // FIXME: what happens when global rule can't be find (error)?
    if (!thisGlobalRule) {
        return <div>{t('Network Rule could not be found')}</div>;
    }

    if (listAPI.isError) return <>{t('Error Fetching', { fieldName: 'Lists' })}</>;
    if (listAPI.isLoading || listAPI.isFetching)
        return (
            <div className={styles.spinner}>
                <Loading />
            </div>
        );

    return (
        <>
            <SplitViewHeader dataTestId={dataTestId}>
                <SplitViewHeader.Title dataTestId={`${dataTestId}-title`} title={t('Network Rule Details')} />
                <SplitViewHeader.Close dataTestId={`${dataTestId}-close`} closeSplitView={closeSplitView} />
            </SplitViewHeader>
            <SplitView.Navigation
                dataTestId='splitview-nav'
                selectedRow={selectedRow}
                rows={rows}
                toggleRowSelected={toggleRowSelected}
            />
            <Paper className={styles.contentPanel}>
                <GlobalRuleDetails
                    ruleIsActive={ruleIsActive}
                    dataTestId={dataTestId}
                    globalRule={thisGlobalRule as Rule}
                    ruleTypeString={t('Network')}
                />
            </Paper>
            {showTenantGroupPaper && (
                <TenantGroupOverridePaper
                    rule={original as Rule}
                    key={selectedRow?.id}
                    paperClassName={clsx(styles.contentPanel, styles.marginTop)}
                    dataTestId={dataTestId}
                    readOnly={readOnly}
                    setInitialFormState={setInitialFormState}
                    splitViewFormState={splitViewFormState}
                    setIsOpen={setIsOpen}
                    selectedRowId={selectedRow.id}
                    tenantGroupId={tenantGroupIdString || ''}
                    mode={mode}
                />
            )}

            <SplitRuleStatement
                outputDescription={thisGlobalRule?.outputDescription}
                ruleInputs={thisGlobalRule?.inputs}
                parameters={thisGlobalRule?.parameters}
                lists={thisGlobalRule?.lists}
                listData={listAPI.data?.result}
                conclusionValues={thisGlobalRule._formatted?.conclusionValues}
                expression={thisGlobalRule?.expression}
            />
            <SplitRevisionHistory
                level='TENANT_GROUP'
                // TODO: ZELLE - pass in deployment version if we support revisions tab for tenant group overrides
                key={original?.id}
                ruleId={original?.id}
                listData={listAPI.data?.result}
                tenantGroupId={currentTenantGroupId}
            />
        </>
    );
};

export default SplitViewContent;
